import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";

const FIELD_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  identifier_value: "Identifier",
  state: "State",
  county: "County",
  court_district: "Court jurisdiction",
  case_filed_date: "Original filing/recording date",
  city: "City",
  account_number: "Account number",
  utility_company: "Utility company",
  utility_type: "Utility type",
  date_of_birth: "Date of birth",
  date_filed: "Date filed/recorded",
  ticket_date: "Ticket date",
  issue_date: "Warrant issued date",
  address: "Address",
  expiration_date: "Date of expiration",
};

const REASON_LABELS = {
  matched: "Matched",
  different: "Values differ",
  not_found_in_document: "Not found in document",
};

export const MismatchReviewModal = ({ order, open, onOpenChange, onConfirmed }) => {
  const [finalData, setFinalData] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const details = useMemo(() => order?.comparison?.details || {}, [order]);
  const fieldNames = useMemo(() => {
    const keys = Object.keys(details);
    if (keys.length) return keys;
    // Extraction failed entirely: fall back to the member's entered fields so
    // they can still review and confirm manually.
    const entered = order?.entered || {};
    return Object.keys(FIELD_LABELS).filter((key) => String(entered[key] || "").trim() !== "");
  }, [details, order]);

  useEffect(() => {
    if (order) {
      const entered = order.entered || {};
      const initial = {};
      fieldNames.forEach((field) => {
        const row = details[field];
        // Autofill preference: what the DOCUMENT says (extracted) first, so the
        // member confirms the information pulled from their upload; fall back to
        // their manually entered value when extraction found nothing.
        initial[field] = (row && (row.extracted || row.entered)) || entered[field] || "";
      });
      setFinalData(initial);
      setAcknowledged(false);
    }
  }, [order, details, fieldNames]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/orders/${order.id}/confirm`, {
        acknowledgement: true,
        finalData,
      });
      toast.success("Final values confirmed. The request is now eligible for checkout.");
      onOpenChange(false);
      onConfirmed?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Confirmation failed."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-popover border-border" data-testid="mismatch-review-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Review &amp; confirm order {order.id.slice(0, 8)}</DialogTitle>
          <DialogDescription>
            {order.verification_status === "verified"
              ? "Every field matched the uploaded document. Review the extracted information below and confirm its accuracy to make this request report-eligible."
              : "The document extraction found differences from your entry. Compare each field, correct the final values, and confirm their accuracy."}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[52vh] pr-3">
          <div className="space-y-3">
            {fieldNames.map((field) => {
              const row = details[field] || {
                entered: order.entered?.[field] || "",
                extracted: "",
                match: false,
                reason: "not_found_in_document",
              };
              const matched = row.match;
              return (
                <div
                  key={field}
                  data-testid={`mismatch-field-${field}`}
                  className={`rounded-lg border border-border p-4 border-l-2 ${
                    matched
                      ? "border-l-[hsl(var(--status-verified))] bg-[hsl(var(--status-verified)/0.06)]"
                      : "border-l-[hsl(var(--status-mismatch))] bg-[hsl(var(--status-mismatch)/0.08)]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{FIELD_LABELS[field] || field}</p>
                    <span
                      className={`text-xs font-medium ${
                        matched ? "text-[hsl(var(--status-verified))]" : "text-[hsl(var(--status-mismatch))]"
                      }`}
                    >
                      {REASON_LABELS[row.reason] || row.reason}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-xs text-muted-foreground">You entered</p>
                      <p className="mt-0.5 font-mono text-sm">{row.entered || <span className="text-muted-foreground">—</span>}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Document shows</p>
                      <p className="mt-0.5 font-mono text-sm">
                        {row.extracted || <span className="text-muted-foreground">Not found</span>}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <Label htmlFor={`final-${field}`} className="text-xs">
                      Final value
                    </Label>
                    <Input
                      id={`final-${field}`}
                      data-testid={`mismatch-final-${field}-input`}
                      value={finalData[field] || ""}
                      onChange={(e) => setFinalData((previous) => ({ ...previous, [field]: e.target.value }))}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex items-start gap-2.5 rounded-lg border border-border bg-secondary/40 p-3">
          <Checkbox
            id="acknowledgement"
            checked={acknowledged}
            onCheckedChange={(value) => setAcknowledged(value === true)}
            data-testid="mismatch-acknowledgement-checkbox"
          />
          <Label htmlFor="acknowledgement" className="text-sm leading-relaxed cursor-pointer">
            I confirm the final values above are accurate for report research, and I take responsibility for their
            correctness.
          </Label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="mismatch-cancel-button">
            Cancel
          </Button>
          <Button
            disabled={!acknowledged || submitting}
            onClick={confirm}
            data-testid="mismatch-review-confirm-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Final Values"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
